import {getFormProps, getInputProps, useForm} from '@conform-to/react'
import {parseWithZod} from '@conform-to/zod'
import {useFetcher} from '@remix-run/react'
import {Button} from '@translated/babele-react'
import React, {useEffect} from 'react'

import {type Memory} from '~/models/memory/memory.server'
import {Dialog, DialogContent, DialogTrigger} from '~/ui/Modal'
import {type DeleteMemoryAction, deleteMemorySchema} from '../_action.memory'

export const DeleteMemoryDialog = ({
  memory,
  children,
}: {
  memory: Memory
  children: React.ReactNode
}) => {
  const fetcher = useFetcher<DeleteMemoryAction>({key: memory.id.toString()})
  const [form, fields] = useForm({
    onValidate: ({formData}) => {
      return parseWithZod(formData, {schema: deleteMemorySchema})
    },
    lastResult: fetcher.data,
    id: 'delete-memory',
    defaultValue: {id: memory.id.toString()},
  })
  const [isDeleteMemoryModalOpen, setIsDeleteMemoryModalOpen] =
    React.useState(false)

  const isSuccess = fetcher.data?.status == 'success'

  useEffect(() => {
    if (isSuccess) setIsDeleteMemoryModalOpen(false)
  }, [isSuccess])

  return (
    <Dialog
      open={isDeleteMemoryModalOpen}
      onOpenChange={setIsDeleteMemoryModalOpen}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <fetcher.Form {...getFormProps(form)} action="/memory" method="POST">
          <div className="flex flex-col gap-8 py-6">
            <h2 className="text-display-md text-center">Delete memory</h2>
            <div className="text-center">
              Are you sure you want to delete {memory.name}?
            </div>
            <input {...getInputProps(fields.id, {type: 'hidden'})} />
            {form.errors && (
              <div className="p-4 bg-critical-100 text-critical-800">
                {form.errors}
              </div>
            )}
            <Button
              isLoading={fetcher.state != 'idle'}
              variant={{tone: 'critical'}}
              type="submit"
              name="_action"
              value="deleteMemory"
            >
              Delete
            </Button>
          </div>
        </fetcher.Form>
      </DialogContent>
    </Dialog>
  )
}
